import axios, { type GenericAbortSignal } from 'axios';

import { useAuthStore } from '../store/authStore';
import { getJwtExpiresAtMs } from './kioskAccountInactivity';
import { registerAuthenticatedRequestCanceler } from './kioskAccountSession';
import { getApiBaseUrl } from './apiUrl';
import { affectsGlobalSearch, invalidateGlobalSearch } from '../features/globalSearch/invalidation';

/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any -- Axios's merged generic must match its declaration. */
declare module 'axios' {
  interface InternalAxiosRequestConfig<D = any> {
    authRequestController?: AbortController;
    authRequestEpoch?: number;
    authRequestToken?: string;
  }
}
/* eslint-enable @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any */

let authRequestEpoch = 0;
const authenticatedRequestControllers = new Set<AbortController>();

function combineAbortSignals(existing: GenericAbortSignal | undefined, added: AbortSignal) {
  if (!existing) return added;
  const combined = new AbortController();
  const abort = () => combined.abort();
  if (existing.aborted || added.aborted) abort();
  else {
    existing.addEventListener?.('abort', abort, { once: true });
    added.addEventListener('abort', abort, { once: true });
  }
  return combined.signal;
}

function releaseAuthenticatedRequest(config?: { authRequestController?: AbortController }) {
  if (config?.authRequestController) {
    authenticatedRequestControllers.delete(config.authRequestController);
  }
}

function isCurrentPasswordRejection(error: {
  config?: { url?: string };
  response?: { status?: number; data?: unknown };
}) {
  const responseData = error.response?.data;
  return (
    error.response?.status === 401 &&
    error.config?.url?.endsWith('/auth/change-password') === true &&
    typeof responseData === 'object' &&
    responseData !== null &&
    'message' in responseData &&
    responseData.message === 'Current password is incorrect'
  );
}

export function cancelAuthenticatedRequests() {
  authRequestEpoch += 1;
  authenticatedRequestControllers.forEach((controller) => controller.abort());
  authenticatedRequestControllers.clear();
}
registerAuthenticatedRequestCanceler(cancelAuthenticatedRequests);

export const api = axios.create({
  baseURL: getApiBaseUrl(),
});

api.interceptors.request.use((request) => {
  const token = useAuthStore.getState().token;
  if (token) {
    const controller = new AbortController();
    authenticatedRequestControllers.add(controller);
    request.headers.Authorization = `Bearer ${token}`;
    request.authRequestController = controller;
    request.authRequestEpoch = authRequestEpoch;
    request.authRequestToken = token;
    request.signal = combineAbortSignals(request.signal, controller.signal);
  }

  return request;
});

api.interceptors.response.use(
  (response) => {
    releaseAuthenticatedRequest(response.config);
    if (
      response.config.authRequestEpoch !== undefined &&
      response.config.authRequestEpoch !== authRequestEpoch
    ) {
      return Promise.reject(new axios.CanceledError('Authenticated session changed'));
    }
    if (
      response.config.authRequestToken &&
      response.config.authRequestToken === useAuthStore.getState().token &&
      affectsGlobalSearch(response.config.method, response.config.url)
    ) {
      invalidateGlobalSearch();
    }
    return response;
  },
  (error) => {
    releaseAuthenticatedRequest(error.config);
    if (
      error.config?.authRequestEpoch !== undefined &&
      error.config.authRequestEpoch !== authRequestEpoch
    ) {
      return Promise.reject(new axios.CanceledError('Authenticated session changed'));
    }
    if (error.response?.status === 401 && !isCurrentPasswordRejection(error)) {
      const authStore = useAuthStore.getState();
      const requestToken = String(error.config?.headers?.Authorization ?? '').replace(
        /^Bearer /,
        '',
      );

      // Ignore stale 401s from requests sent with a token that has already been replaced.
      if (requestToken && authStore.token === requestToken) {
        if (authStore.supportSession) {
          void authStore.restoreSupportSession();
        } else {
          const expiresAt = getJwtExpiresAtMs(requestToken);
          void authStore.clearSession(
            authStore.sessionKind === 'kiosk' && expiresAt !== null && expiresAt <= Date.now()
              ? 'TOKEN_EXPIRED'
              : 'EXPLICIT_CLEAR',
          );
        }
      }
    }

    return Promise.reject(error);
  },
);
